/**
 * Copyright 2022-2024, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { validate } from '../../utils/json_schema_validator';
import { OdpResponseSchema } from './odp_response_schema';
/**
 * Expected value for a qualified/valid segment
 */
const QUALIFIED = 'qualified';
/**
 * Return value when no valid segments found
 */
const EMPTY_SEGMENTS_COLLECTION = [];
/**
 * Return value for scenarios with no valid JSON
 */
const EMPTY_JSON_RESPONSE = null;
/**
 * Standard message for audience querying fetch errors
 */
const AUDIENCE_FETCH_FAILURE_MESSAGE = 'Audience segments fetch failed';
export const LOGGER_NAME = 'OdpSegmentApiManager';
export class DefaultOdpSegmentApiManager {
    constructor(requestHandler, logger) {
        /**
         * Converts the query parameters to a GraphQL JSON payload
         * @returns GraphQL JSON string
         */
        this.toGraphQLJson = (userKey, userValue, segmentsToCheck) => [
            '{"query" : "query {customer',
            `(${userKey} : \\"${userValue}\\") `,
            '{audiences',
            '(subset: [',
            ...((segmentsToCheck === null || segmentsToCheck === void 0 ? void 0 : segmentsToCheck.map((segment, index) => `\\"${segment}\\"${index < segmentsToCheck.length - 1 ? ',' : ''}`)) || ''),
            ']) {edges {node {name state}}}}}"}',
        ].join('');
        this.requestHandler = requestHandler;
        if (logger) {
            this.setLogger(logger);
        }
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
    }
    /**
     * Retrieves the audience segments from ODP
     * @param apiKey ODP public key
     * @param apiHost Host of ODP endpoint
     * @param userKey 'vuid' or 'fs_user_id key'
     * @param userValue Associated value to query for the user key
     * @param segmentsToCheck Audience segments to check for experiment inclusion
     */
    fetchSegments(apiKey, apiHost, userKey, userValue, segmentsToCheck) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return __awaiter(this, void 0, void 0, function* () {
            if ((segmentsToCheck === null || segmentsToCheck === void 0 ? void 0 : segmentsToCheck.length) === 0) {
                return EMPTY_SEGMENTS_COLLECTION;
            }
            const endpoint = `${apiHost}/v3/graphql`;
            const query = this.toGraphQLJson(userKey, userValue, segmentsToCheck);
            const segmentsResponse = yield this.querySegments(apiKey, endpoint, query);
            if (!segmentsResponse) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(`${AUDIENCE_FETCH_FAILURE_MESSAGE} (network error)`);
                return null;
            }
            const parsedSegments = this.parseSegmentsResponseJson(segmentsResponse);
            if (!parsedSegments) {
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error(`${AUDIENCE_FETCH_FAILURE_MESSAGE} (decode error)`);
                return null;
            }
            if (((_c = parsedSegments.errors) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                const { code, classification } = parsedSegments.errors[0].extensions;
                if (code == 'INVALID_IDENTIFIER_EXCEPTION') {
                    (_d = this.logger) === null || _d === void 0 ? void 0 : _d.error(`${AUDIENCE_FETCH_FAILURE_MESSAGE} (invalid identifier)`);
                }
                else {
                    (_e = this.logger) === null || _e === void 0 ? void 0 : _e.error(`${AUDIENCE_FETCH_FAILURE_MESSAGE} (${classification})`);
                }
                return null;
            }
            const edges = (_h = (_g = (_f = parsedSegments === null || parsedSegments === void 0 ? void 0 : parsedSegments.data) === null || _f === void 0 ? void 0 : _f.customer) === null || _g === void 0 ? void 0 : _g.audiences) === null || _h === void 0 ? void 0 : _h.edges;
            if (!edges) {
                (_j = this.logger) === null || _j === void 0 ? void 0 : _j.error(`${AUDIENCE_FETCH_FAILURE_MESSAGE} (decode error)`);
                return null;
            }
            return edges.filter(edge => edge.node.state == QUALIFIED).map(edge => edge.node.name);
        });
    }
    /**
     * Handler for querying the ODP GraphQL endpoint
     * @param apiKey ODP API key
     * @param endpoint Fully-qualified GraphQL endpoint URL
     * @param userKey 'vuid' or 'fs_user_id'
     * @param userValue userKey's value
     * @param query GraphQL formatted query string
     * @returns JSON response string from ODP or null
     */
    querySegments(apiKey, endpoint, query) {
        return __awaiter(this, void 0, void 0, function* () {
            const method = 'POST';
            const url = endpoint;
            const headers = {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
            };
            try {
                const request = this.requestHandler.makeRequest(url, headers, method, query);
                const { statusCode, body } = yield request.responsePromise;
                if (!(statusCode >= 200 && statusCode < 300)) {
                    return null;
                }
                return body;
            }
            catch (_a) {
                return null;
            }
        });
    }
    /**
     * Parses JSON response
     * @param jsonResponse JSON response from ODP
     * @private
     * @returns Response Strongly-typed ODP Response object
     */
    parseSegmentsResponseJson(jsonResponse) {
        let jsonObject = {};
        try {
            jsonObject = JSON.parse(jsonResponse);
        }
        catch (_a) {
            return EMPTY_JSON_RESPONSE;
        }
        if (validate(jsonObject, OdpResponseSchema, false)) {
            return jsonObject;
        }
        return EMPTY_JSON_RESPONSE;
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=odp_segment_api_manager.js.map